import { ChevronDown, LayoutDashboard, LogOut, MessageSquareText } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useAppAuth } from "@/auth/use-app-auth";
import { demoResultsPath } from "@/lib/apex25AppUrl";

import { formatUntil } from "./last-demo";
import { useLatestDemo } from "./use-latest-demo";

// Signing out lands on the Apex 25 home. Clerk only redirects within this origin, so this is the
// in-app path, which production recruit.theresume.io forwards to tryapex25.com.
const SIGNED_OUT_PATH = "/apex25";

/**
 * Replaces the header's "Log in / Sign up" for a signed-in visitor on recruit.theresume.io (and
 * staging, localhost). Kept out of Apex25Chrome because that header also runs on tryapex25.com,
 * which has no Clerk.
 */
export const Apex25AccountMenu = () => {
  const { user, logout } = useAppAuth();
  const latest = useLatestDemo();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="a25-account">
      <button
        type="button"
        className="login a25-account-button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
      >
        Account <ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div className="a25-account-menu">
          {user?.email && <p className="a25-account-email">{user.email}</p>}
          {latest && (
            <a href={demoResultsPath(latest.sessionId)}>
              <MessageSquareText size={15} aria-hidden="true" />
              <span>
                Your demo results
                <small className="a25-account-note">until {formatUntil(latest.expiresAt)}</small>
              </span>
            </a>
          )}
          <a href="/dashboard">
            <LayoutDashboard size={15} aria-hidden="true" /> Go to dashboard
          </a>
          <button
            type="button"
            onClick={() => void logout({ logoutParams: { returnTo: SIGNED_OUT_PATH } })}
          >
            <LogOut size={15} aria-hidden="true" /> Log out
          </button>
        </div>
      )}
    </div>
  );
};
